import { useState } from 'react';

import {
  usePaymentListQuery,
  useInvoiceListQuery,
  useTransactionListQuery,
  useSubscriptionListQuery,
  useOrderListQuery,
  useRefundRequestListQuery,
  useWalletLookupQuery,
  useWalletLedgerQuery,
  usePaymentMethodsQuery,
  usePaymentDetailQuery,
  useInvoiceDetailQuery,
} from '@/client/api/payment/payment.query';
import { formatMoney } from '@/client/types/payment';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

import DataTable, { Column } from '../components/DataTable';
import BackendPendingNotice from '../components/BackendPendingNotice';

const fmtDateTime = (v?: string | null) => (v ? new Date(v).toLocaleString() : '—');

/** Shared key/value detail body for the payment & invoice dialogs. */
const DetailRows = ({
  loading,
  rows,
}: {
  loading: boolean;
  rows: { label: string; value: React.ReactNode }[];
}) =>
  loading ? (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map(i => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  ) : (
    <dl className="divide-y divide-gray-100">
      {rows.map(r => (
        <div key={r.label} className="grid grid-cols-3 gap-3 py-2">
          <dt className="text-sm text-gray-500">{r.label}</dt>
          <dd className="col-span-2 break-words text-sm text-gray-900">{r.value}</dd>
        </div>
      ))}
    </dl>
  );

const yesNo = (v: unknown) => (v ? 'Yes' : 'No');

const PaymentDetailDialog = ({
  pid,
  open,
  onOpenChange,
}: {
  pid: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data: p, isLoading } = usePaymentDetailQuery(pid, open && pid != null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Payment {p?.ffid ?? (pid != null ? `#${pid}` : '')}</DialogTitle>
        </DialogHeader>
        <DetailRows
          loading={isLoading}
          rows={[
            { label: 'Amount', value: formatMoney(p?.amount, p?.ccode) },
            { label: 'Amount paid', value: formatMoney(p?.amount_paid, p?.ccode) },
            { label: 'Status', value: <StatusPill value={p?.status ?? ''} /> },
            { label: 'Reference', value: p?.ref_id ?? '—' },
            { label: 'Verified', value: yesNo(p?.verified) },
            { label: 'Fulfilled', value: yesNo(p?.fulfilled) },
            { label: 'Canceled', value: yesNo(p?.canceled) },
            { label: 'Refunded', value: yesNo(p?.refund) },
            { label: 'Invoice', value: p?.invoice_ffid ?? (p?.invid != null ? `invid ${p.invid}` : '—') },
            { label: 'Created', value: fmtDateTime(p?.date_added) },
            { label: 'Notes', value: p?.ref_notes ?? '—' },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
};

const InvoiceDetailDialog = ({
  invid,
  open,
  onOpenChange,
}: {
  invid: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data: inv, isLoading } = useInvoiceDetailQuery(invid, open && invid != null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invoice {inv?.ffid ?? (invid != null ? `#${invid}` : '')}</DialogTitle>
        </DialogHeader>
        <DetailRows
          loading={isLoading}
          rows={[
            { label: 'Status', value: <StatusPill value={inv?.status ?? ''} /> },
            { label: 'Billing type', value: inv?.billing_type ?? '—' },
            { label: 'Amount', value: formatMoney(inv?.amount, inv?.ccode) },
            { label: 'Tax', value: formatMoney(inv?.amount_tax, inv?.ccode) },
            { label: 'Discount', value: formatMoney(inv?.amount_discount, inv?.ccode) },
            { label: 'Refunded', value: formatMoney(inv?.amount_refund, inv?.ccode) },
            { label: 'Amount due', value: formatMoney(inv?.amount_due, inv?.ccode) },
            { label: 'Paid', value: yesNo(inv?.paid) },
            { label: 'Created', value: fmtDateTime(inv?.date_added) },
            { label: 'Due', value: fmtDateTime(inv?.date_due) },
            { label: 'Paid at', value: fmtDateTime(inv?.date_paid) },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
};

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

const StatusPill = ({ value }: { value?: string | number | null }) => {
  const s = String(value ?? '').toLowerCase();
  const tone =
    /paid|success|active|fulfilled|verified|complete/.test(s)
      ? 'bg-emerald-50 text-emerald-700'
      : /pending|processing|trial/.test(s)
      ? 'bg-amber-50 text-amber-700'
      : /fail|cancel|refund|declin|expired/.test(s)
      ? 'bg-red-50 text-red-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {value ?? '—'}
    </span>
  );
};

type Sub =
  | 'wallet'
  | 'payments'
  | 'invoices'
  | 'transactions'
  | 'subscriptions'
  | 'orders'
  | 'refunds'
  | 'escrow';

const SUBS: { key: Sub; label: string }[] = [
  { key: 'wallet', label: 'Wallet & cards' },
  { key: 'payments', label: 'Payments' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'orders', label: 'Orders' },
  { key: 'refunds', label: 'Refunds' },
  { key: 'escrow', label: 'Escrow' },
];

const PaymentsTab = ({ uid }: { uid: number }) => {
  const [sub, setSub] = useState<Sub>('wallet');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {SUBS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSub(key)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              sub === key
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {sub === 'wallet' && <WalletSection uid={uid} />}
      {sub === 'payments' && <PaymentsSection uid={uid} />}
      {sub === 'invoices' && <InvoicesSection uid={uid} />}
      {sub === 'transactions' && <TransactionsSection uid={uid} />}
      {sub === 'subscriptions' && <SubscriptionsSection uid={uid} />}
      {sub === 'orders' && <OrdersSection uid={uid} />}
      {sub === 'refunds' && <RefundsSection uid={uid} />}
      {sub === 'escrow' && (
        <BackendPendingNotice
          title="Escrow"
          needed="No escrow API hooks exist in the admin app, and there is no user-scoped escrow endpoint. Needs an escrow service surface (or exposure through the payments/orders endpoints) keyed by uid."
        />
      )}
    </div>
  );
};

/* ── Wallet & cards ── */
const WalletSection = ({ uid }: { uid: number }) => {
  const { data: wallet, isLoading } = useWalletLookupQuery({ uid });
  const wallets = wallet?.wallets ?? [];
  const primaryWid = wallets[0]?.wid ?? null;
  const { data: ledger, isLoading: ledgerLoading } = useWalletLedgerQuery(primaryWid, { limit: 25 });
  const { data: cards = [], isLoading: cardsLoading } = usePaymentMethodsQuery({ uid });

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Wallets</h3>
        {isLoading ? (
          <div className="h-16 animate-pulse rounded-lg bg-gray-100" />
        ) : wallets.length === 0 ? (
          <p className="text-sm italic text-gray-400">No wallet for this user.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {wallets.map(w => (
              <div key={w.wid} className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-xs text-gray-500">Balance ({w.ccode ?? 'GHS'})</p>
                <p className="mt-1 text-xl font-bold text-gray-900">
                  {formatMoney(w.balance, w.ccode)}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  wid {w.wid}
                  {w.hold ? ' · on hold' : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Saved cards</h3>
        <DataTable
          loading={cardsLoading}
          rows={cards}
          keyFn={c => c.bpmid}
          emptyLabel="No saved payment methods."
          columns={[
            { header: 'Brand', cell: c => c.brand ?? c.card_type ?? c.label ?? '—' },
            { header: 'Last 4', cell: c => (c.last4 ? `•••• ${c.last4}` : '—') },
            {
              header: 'Expiry',
              cell: c => (c.exp_month && c.exp_year ? `${c.exp_month}/${c.exp_year}` : '—'),
            },
            { header: 'Provider', cell: c => c.provider ?? c.channel ?? '—' },
            { header: 'Default', cell: c => (c.is_default ? 'Yes' : '—') },
          ]}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Wallet ledger</h3>
        <DataTable
          loading={ledgerLoading}
          rows={ledger?.transactions ?? []}
          keyFn={t => t.wtid}
          emptyLabel={primaryWid ? 'No wallet transactions.' : 'No wallet to show a ledger for.'}
          columns={[
            { header: 'Date', cell: t => fmtDate(t.date_added) },
            { header: 'Type', cell: t => t.type ?? '—' },
            { header: 'Amount', cell: t => formatMoney(t.amount) },
            { header: 'Balance after', cell: t => formatMoney(t.balance_after) },
            { header: 'Description', cell: t => t.description ?? '—' },
          ]}
        />
      </section>
    </div>
  );
};

/* ── Payments ── */
const PaymentsSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = usePaymentListQuery({ uid });
  const [selPid, setSelPid] = useState<number | null>(null);
  const columns: Column<any>[] = [
    { header: 'Date', cell: p => fmtDate(p.date_added) },
    { header: 'Ref', cell: p => p.ref_id ?? p.ffid ?? `pid ${p.pid}` },
    { header: 'Amount', cell: p => formatMoney(p.amount, p.ccode) },
    { header: 'Status', cell: p => <StatusPill value={p.status} /> },
    { header: '', cell: () => <span className="text-xs text-blue-600">View →</span> },
  ];
  return (
    <>
      <DataTable
        loading={isLoading}
        rows={data?.payments ?? []}
        keyFn={p => p.pid}
        emptyLabel="No payments for this user."
        columns={columns}
        onRowClick={p => setSelPid(p.pid)}
      />
      <PaymentDetailDialog
        pid={selPid}
        open={selPid != null}
        onOpenChange={o => !o && setSelPid(null)}
      />
    </>
  );
};

/* ── Invoices ── */
const InvoicesSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useInvoiceListQuery({ uid });
  const [selInvid, setSelInvid] = useState<number | null>(null);
  return (
    <>
      <DataTable
        loading={isLoading}
        rows={data?.invoices ?? []}
        keyFn={i => i.invid}
        emptyLabel="No invoices for this user."
        onRowClick={i => setSelInvid(i.invid)}
        columns={[
          { header: 'Date', cell: i => fmtDate(i.date_added) },
          { header: 'Invoice', cell: i => i.ffid ?? `invid ${i.invid}` },
          { header: 'Amount', cell: i => formatMoney(i.amount, i.ccode) },
          { header: 'Due', cell: i => formatMoney(i.amount_due, i.ccode) },
          { header: 'Status', cell: i => <StatusPill value={i.status} /> },
          { header: '', cell: () => <span className="text-xs text-blue-600">View →</span> },
        ]}
      />
      <InvoiceDetailDialog
        invid={selInvid}
        open={selInvid != null}
        onOpenChange={o => !o && setSelInvid(null)}
      />
    </>
  );
};

/* ── Transactions ── */
const TransactionsSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useTransactionListQuery({ uid });
  return (
    <DataTable
      loading={isLoading}
      rows={data?.transactions ?? []}
      keyFn={t => t.tid}
      emptyLabel="No ledger transactions for this user."
      columns={[
        { header: 'Date', cell: t => fmtDate(t.date_added) },
        { header: 'Type', cell: t => t.ttype ?? (t.ttid != null ? `tt ${t.ttid}` : '—') },
        { header: 'Amount', cell: t => formatMoney(t.amount, t.ccode) },
        { header: 'From→To', cell: t => `${t.uid_from ?? '—'} → ${t.uid_to ?? '—'}` },
        { header: 'Fulfilled', cell: t => (t.fulfilled ? 'Yes' : '—') },
      ]}
    />
  );
};

/* ── Subscriptions ── */
const SubscriptionsSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useSubscriptionListQuery({ uid });
  return (
    <DataTable
      loading={isLoading}
      rows={data?.subscriptions ?? []}
      keyFn={s => s.plid}
      emptyLabel="No subscriptions for this user."
      columns={[
        { header: 'Plan', cell: s => s.plan_name ?? `plid ${s.plid}` },
        { header: 'Amount', cell: s => formatMoney(s.amount, s.ccode) },
        { header: 'Interval', cell: s => s.interval_type ?? '—' },
        { header: 'Started', cell: s => fmtDate(s.date_started) },
        { header: 'Status', cell: s => <StatusPill value={s.billing_status} /> },
      ]}
    />
  );
};

/* ── Orders ── */
const OrdersSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useOrderListQuery({ uid });
  return (
    <DataTable
      loading={isLoading}
      rows={data?.orders ?? []}
      keyFn={o => o.orderid}
      emptyLabel="No orders for this user."
      columns={[
        { header: 'Date', cell: o => fmtDate(o.date_added) },
        { header: 'Order', cell: o => o.ffid ?? `#${o.orderid}` },
        { header: 'Total', cell: o => formatMoney(o.total, o.ccode) },
        { header: 'Status', cell: o => <StatusPill value={o.status} /> },
      ]}
    />
  );
};

/* ── Refunds ── */
const RefundsSection = ({ uid }: { uid: number }) => {
  const { data, isLoading } = useRefundRequestListQuery({ uid });
  return (
    <DataTable
      loading={isLoading}
      rows={data?.refund_requests ?? []}
      keyFn={r => r.rrid}
      emptyLabel="No refund requests for this user."
      columns={[
        { header: 'Date', cell: r => fmtDate(r.date_added) },
        { header: 'Order', cell: r => (r.orderid != null ? `#${r.orderid}` : '—') },
        { header: 'Amount', cell: r => formatMoney(r.amount_requested) },
        { header: 'Reason', cell: r => r.reason ?? '—' },
        { header: 'Status', cell: r => <StatusPill value={r.status} /> },
      ]}
    />
  );
};

export default PaymentsTab;
